import type { Store } from "../state.js";
import type { GameClient } from "../net/client.js";
import { el } from "./dom.js";

export function renderEmailScreen(store: Store, client: GameClient): HTMLElement {
  const screen = store.get().screen;
  if (screen.kind !== "auth_email") throw new Error("wrong screen");

  const card = el("div", { class: "card" });
  const logo = el("h1", { class: "logo" }, "Vinyl");
  const tagline = el("p", { class: "tagline" }, "A small world. A long road.");

  const form = el("form", { class: "form" });
  const label = el("label", { class: "field-label", for: "email" }, "Enter your email to begin");
  const input = el("input", {
    type: "email",
    id: "email",
    class: "input",
    autocomplete: "email",
    autofocus: true,
    required: true,
    placeholder: "you@example.com",
  });
  const errorBox = el("div", { class: "error" }, screen.error ?? "");
  if (!screen.error) errorBox.style.display = "none";

  const button = el(
    "button",
    {
      type: "submit",
      class: "btn",
      disabled: screen.submitting,
    },
    screen.submitting ? "Sending…" : "Send code",
  );

  form.append(label, input, errorBox, button);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const email = input.value.trim();
    if (!email) return;
    store.setScreen({ kind: "auth_email", submitting: true });
    client.send({ t: "requestOtp", d: { email } });
  });

  card.append(logo, tagline, form);
  return wrap(card);
}

export function renderOtpScreen(store: Store, client: GameClient): HTMLElement {
  const screen = store.get().screen;
  if (screen.kind !== "auth_otp") throw new Error("wrong screen");

  const card = el("div", { class: "card" });
  const logo = el("h1", { class: "logo" }, "Vinyl");
  const subtitle = el(
    "p",
    { class: "tagline" },
    `We sent a 6-digit code to ${screen.email}.`,
  );

  if (screen.devCode) {
    const devNotice = el(
      "div",
      { class: "dev-notice" },
      "Dev mode — code: ",
      el("strong", {}, screen.devCode),
    );
    card.append(devNotice);
  }

  const form = el("form", { class: "form" });
  const codeInput = el("input", {
    type: "text",
    inputmode: "numeric",
    pattern: "[0-9]*",
    maxlength: 6,
    class: "input input-otp",
    autocomplete: "one-time-code",
    autofocus: true,
    placeholder: "••••••",
  });
  codeInput.addEventListener("input", () => {
    codeInput.value = codeInput.value.replace(/\D/g, "").slice(0, 6);
    if (codeInput.value.length === 6) form.requestSubmit();
  });

  const rememberWrap = el("label", { class: "remember" });
  const remember = el("input", { type: "checkbox", checked: screen.rememberMe });
  rememberWrap.append(remember, document.createTextNode(" Remember me on this device"));

  const errorBox = el("div", { class: "error" }, screen.error ?? "");
  if (!screen.error) errorBox.style.display = "none";

  const button = el(
    "button",
    {
      type: "submit",
      class: "btn",
      disabled: screen.submitting,
    },
    screen.submitting ? "Verifying…" : "Enter the world",
  );

  const back = el("button", { type: "button", class: "btn-link" }, "Use a different email");
  back.addEventListener("click", () => {
    store.setScreen({ kind: "auth_email", submitting: false });
  });

  form.append(codeInput, rememberWrap, errorBox, button, back);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (codeInput.value.length !== 6) return;
    store.setScreen({ ...screen, submitting: true, error: undefined });
    client.send({
      t: "verifyOtp",
      d: { email: screen.email, code: codeInput.value, rememberMe: remember.checked },
    });
  });

  card.append(logo, subtitle, form);
  return wrap(card);
}

function wrap(card: HTMLElement): HTMLElement {
  const stage = el("div", { class: "stage" });
  stage.append(card);
  return stage;
}
